import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, UserPlus, Save, CheckCircle, Trash2, X } from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface AdminRole {
  id: string;
  user_id: string;
  email: string;
  role: string;
  display_name: string | null;
  created_at: string;
}

interface Permission {
  role: string;
  module: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  super_admin: { label: 'Super Admin', color: 'badge-danger' },
  admin: { label: 'Admin', color: 'badge-warning' },
  content_manager: { label: 'Content Manager', color: 'badge-info' },
  support: { label: 'Support', color: 'badge-success' },
  finance: { label: 'Finanzen', color: 'badge-neutral' },
  analytics: { label: 'Analytics', color: 'badge-neutral' },
};

const MODULE_LABELS: Record<string, string> = {
  users: 'Nutzerverwaltung', subscriptions: 'Abos', questions: 'Fragen',
  chapters: 'Kapitel', images: 'Bilder', badges: 'Badges',
  ai_dashboard: 'KI Dashboard', ai_prompts: 'KI Prompts', analytics: 'Analytics',
  gamification: 'Gamification', settings: 'Einstellungen', feature_flags: 'Feature Flags',
  security: 'Sicherheit', audit_log: 'Audit Log', support: 'Support', reports: 'Reports',
};

export const RolesPage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminRole[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [inviteName, setInviteName] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'admins' | 'permissions'>('admins');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const [adminsRes, permsRes] = await Promise.all([
      adminDb.from('admin_roles').select('*').order('created_at'),
      adminDb.from('role_permissions').select('*').order('role, module'),
    ]);
    setAdmins(adminsRes.data || []);
    setPermissions(permsRes.data || []);
    setLoading(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setSaving(true);
    // NOTE: Full admin user creation requires the Supabase Auth Admin API (server-side).
    // This creates the role entry as a placeholder. The user_id must be updated after
    // the admin invites the user via Supabase Dashboard or /api/admin/invite endpoint.
    const { error } = await adminDb.from('admin_roles').insert({
      email: inviteEmail,
      role: inviteRole,
      display_name: inviteName || inviteEmail.split('@')[0],
      // user_id intentionally omitted — will be linked when user accepts invite
    });

    if (error) {
      alert(`Fehler: ${error.message}`);
      setSaving(false);
      return;
    }

    const session = await supabase.auth.getSession();
    await adminDb.from('audit_logs').insert({
      admin_email: session.data.session?.user?.email || 'unknown',
      action: 'rbac.invite',
      resource_type: 'admin_role',
      new_value: { email: inviteEmail, role: inviteRole },
    });

    setSaving(false);
    setShowInvite(false);
    setInviteEmail('');
    setInviteName('');
    await load();
  };

  const handleRoleChange = async (admin: AdminRole, newRole: string) => {
    await adminDb.from('admin_roles').update({ role: newRole, updated_at: new Date().toISOString() }).eq('id', admin.id);
    const session = await supabase.auth.getSession();
    await adminDb.from('audit_logs').insert({
      admin_email: session.data.session?.user?.email || 'unknown',
      action: 'rbac.role_change',
      resource_type: 'admin_role',
      resource_id: admin.email,
      old_value: { role: admin.role },
      new_value: { role: newRole },
    });
    await load();
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><ShieldCheck size={24} /> Rollenverwaltung</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  // Group permissions by role
  const permsByRole: Record<string, Permission[]> = {};
  permissions.forEach(p => {
    if (!permsByRole[p.role]) permsByRole[p.role] = [];
    permsByRole[p.role].push(p);
  });

  return (
    <div>
      <div className="page-header">
        <h1><ShieldCheck size={24} /> Rollenverwaltung</h1>
        <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
          <UserPlus size={16} /> Admin einladen
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button className={`btn ${activeTab === 'admins' ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setActiveTab('admins')}>
          Admins ({admins.length})
        </button>
        <button className={`btn ${activeTab === 'permissions' ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setActiveTab('permissions')}>
          Berechtigungsmatrix
        </button>
      </div>

      {activeTab === 'admins' && (
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Seit</th></tr>
            </thead>
            <tbody>
              {admins.map(admin => {
                const rc = ROLE_LABELS[admin.role] || { label: admin.role, color: 'badge-neutral' };
                return (
                  <tr key={admin.id}>
                    <td style={{ fontWeight: 600 }}>{admin.display_name || '—'}</td>
                    <td className="text-sm">{admin.email}</td>
                    <td>
                      <select
                        className="input"
                        style={{ width: 170, fontSize: 13 }}
                        value={admin.role}
                        onChange={e => handleRoleChange(admin, e.target.value)}
                      >
                        {Object.entries(ROLE_LABELS).map(([key, val]) => (
                          <option key={key} value={key}>{val.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-xs text-muted">{new Date(admin.created_at).toLocaleDateString('de-DE')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="card" style={{ overflow: 'auto' }}>
          <table style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Modul</th>
                {Object.entries(ROLE_LABELS).map(([key, val]) => (
                  <th key={key} style={{ textAlign: 'center', minWidth: 90 }}>{val.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(MODULE_LABELS).map(([mod, label]) => (
                <tr key={mod}>
                  <td style={{ fontWeight: 600 }}>{label}</td>
                  {Object.keys(ROLE_LABELS).map(role => {
                    const perm = permissions.find(p => p.role === role && p.module === mod);
                    if (!perm) return <td key={role} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>—</td>;
                    return (
                      <td key={role} style={{ textAlign: 'center' }}>
                        <div className="flex items-center justify-center gap-1">
                          {perm.can_view && <span title="Lesen" style={{ color: 'var(--success)', fontSize: 11 }}>👁</span>}
                          {perm.can_edit && <span title="Bearbeiten" style={{ color: 'var(--warning)', fontSize: 11 }}>✏️</span>}
                          {perm.can_delete && <span title="Loeschen" style={{ color: 'var(--danger)', fontSize: 11 }}>🗑</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 440, padding: 24 }}>
            <div className="flex items-center justify-between mb-4">
              <h3>Admin einladen</h3>
              <button onClick={() => setShowInvite(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>E-Mail *</label>
                <input className="input" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="admin@fahryo.de" />
              </div>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Name</label>
                <input className="input" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Max Mustermann" />
              </div>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Rolle</label>
                <select className="input" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  {Object.entries(ROLE_LABELS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button className="btn btn-secondary" onClick={() => setShowInvite(false)}>Abbrechen</button>
                <button className="btn btn-primary" onClick={handleInvite} disabled={saving || !inviteEmail}>
                  {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <UserPlus size={14} />}
                  Einladen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
